import React, { useState } from "react";
import { JsonViewer, CollapsibleSection } from "./SharedDebugComponents";
import './apiDebug.css';

export default function DataCronDebug({ data, index }) {
  const [expandedSections, setExpandedSections] = useState({
    basicInfo: false,
    description: false,
    dates: false,
    times: false,
    tasks: false
  });

  const [isAllExpanded, setIsAllExpanded] = useState(false);

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const toggleAllSections = () => {
    const newState = !isAllExpanded;
    setIsAllExpanded(newState);
    setExpandedSections({
      basicInfo: newState,
      description: newState,
      dates: newState,
      times: newState,
      tasks: newState
    });
  };

  if (!data) {
    return (
      <div className="no-data-message">
        <p>No hay datos disponibles</p>
      </div>
    );
  }

  // Calcular información adicional
  const statusBadge = data.active ? 'Activo' : 'Inactivo';
  const progressValue = data.advance || 0;
  const currentTaskIndex = data.currentTask !== undefined ? data.currentTask : null;
  const totalTasks = data.tasks ? Object.keys(data.tasks).length : 0;

  return (
    <div className="api-data-viewer w-100">
      {/* Header del registro */}
      <div className="record-header">
        <div className="record-header-content">
          <h2 className="record-title">
            <span className="record-index">#{index + 1}</span>
            Detalle de Process Monitor
          </h2>
          <div className="record-badges">
            <span className={`status-badge ${data.active ? 'status-active' : 'status-inactive'}`}>
              {statusBadge}
            </span>
            <span className="progress-badge">
              {progressValue}%
            </span>
          </div>
        </div>
        <button 
          className="expand-all-btn"
          onClick={toggleAllSections}
          title={isAllExpanded ? 'Colapsar todo' : 'Expandir todo'}
        >
          {isAllExpanded ? '▼ Colapsar todo' : '▶ Expandir todo'}
        </button>
      </div>
      
      {/* Sección de Información Básica */}
      <CollapsibleSection
        title="Información Básica"
        isExpanded={expandedSections.basicInfo}
        onToggle={() => toggleSection('basicInfo')}
        icon="📋"
        headerColor="#e2e2e2cc"
      >
        <div className="section-content">
          <div className="detail-grid">
            <div className="detail-item">
              <span className="detail-label">ID:</span>
              <span className="detail-value detail-id">{data._id}</span>
            </div>
            <div className="detail-item">
              <span className="detail-label">Code:</span>
              <span className="detail-value detail-code">{data.code}</span>
            </div>
            <div className="detail-item">
              <span className="detail-label">Estado:</span>
              <span className={`status-value ${data.active ? 'active' : 'inactive'}`}>
                {data.active ? '✓ Activo' : '✗ Inactivo'}
              </span>
            </div>
            <div className="detail-item">
              <span className="detail-label">Progreso:</span>
              <div className="progress-container">
                <div className="progress-bar-wrapper">
                  <div 
                    className="progress-bar-fill" 
                    style={{ width: `${progressValue}%` }}
                  ></div>
                </div>
                <span className="progress-text">{progressValue}% completado</span>
              </div>
            </div>
          </div>
        </div>
      </CollapsibleSection>

      {/* Sección de Descripción */}
      {data.name && Array.isArray(data.name) && (
        <CollapsibleSection
          title="Descripción"
          isExpanded={expandedSections.description}
          onToggle={() => toggleSection('description')}
          icon="📝"
          headerColor="#e2e2e242"
        >
          <div className="section-content">
            <div className="description-text">
              {data.name[0]
                ?.replace('%s', data.name[1] || '')
                .replace('%s', data.name[2] || '') || 'Sin descripción'}
            </div>
          </div>
        </CollapsibleSection>
      )}

      {/* Sección de Data */}
      {data.data && (
        <CollapsibleSection
          title="Datos (JSON)"
          isExpanded={expandedSections.dates}
          onToggle={() => toggleSection('dates')}
          icon="💾"
          badge={typeof data.data === 'object' ? 'Objeto' : 'Primitivo'}
          headerColor="#e2e2e2cc"
        >
          <div className="section-content">
            <JsonViewer data={data.data} />
          </div>
        </CollapsibleSection>
      )}

      {/* Sección de Tiempos */}
      <CollapsibleSection
        title="Tiempos"
        isExpanded={expandedSections.times}
        onToggle={() => toggleSection('times')}
        icon="⏰"
        headerColor="#e2e2e2cc"
      >
        <div className="section-content">
          <div className="detail-grid">
            <div className="detail-item">
              <span className="detail-label">Creado:</span>
              <span className="detail-value detail-time">
                {data.createdAt ? new Date(data.createdAt).toLocaleString('es-ES', {
                  year: 'numeric',
                  month: '2-digit',
                  day: '2-digit',
                  hour: '2-digit',
                  minute: '2-digit',
                  second: '2-digit'
                }) : 'N/A'}
              </span>
            </div>
            <div className="detail-item">
              <span className="detail-label">Actualizado:</span>
              <span className="detail-value detail-time">
                {data.updatedAt ? new Date(data.updatedAt).toLocaleString('es-ES', {
                  year: 'numeric',
                  month: '2-digit',
                  day: '2-digit',
                  hour: '2-digit',
                  minute: '2-digit',
                  second: '2-digit'
                }) : 'N/A'}
              </span>
            </div>
            {data.createdAt && data.updatedAt && (
              <div className="detail-item">
                <span className="detail-label">Duración:</span>
                <span className="detail-value">
                  {Math.round((new Date(data.updatedAt) - new Date(data.createdAt)) / 1000)} segundos
                </span>
              </div>
            )}
          </div>
        </div>
      </CollapsibleSection>

      {/* Sección de Tareas */}
      {data.tasks && (
        <CollapsibleSection
          title="Tareas"
          isExpanded={expandedSections.tasks}
          onToggle={() => toggleSection('tasks')}
          icon="✅"
          badge={`${currentTaskIndex !== null ? currentTaskIndex + 1 : 0}/${totalTasks}`}
          headerColor="#e2e2e242"
        >
          <div className="section-content">
            {currentTaskIndex !== null && data.tasks[currentTaskIndex] && (
              <div className="current-task-info">
                <span className="detail-label">Tarea actual:</span>
                <span className="detail-value detail-task-current">
                  {data.tasks[currentTaskIndex]}
                </span>
              </div>
            )}
            <JsonViewer data={data.tasks} title="Lista completa de tareas" />
          </div>
        </CollapsibleSection>
      )}
    </div>
  );
}
